import type {
  NativeCallCapabilities,
  SystemCallAction as NativeSystemCallAction,
} from '@sableclient/tauri-plugin-livekit-mobile';

import { isTauri } from '@tauri-apps/api/core';
import { type as osType } from '@tauri-apps/plugin-os';

import { isNativeMobile } from './os';

export type SystemCallAction = NativeSystemCallAction & {
  notificationEventId?: string;
  hasVideo?: boolean;
};

export function hasNativeCalls(): boolean {
  try {
    return isNativeMobile();
  } catch {
    return false;
  }
}

export async function nativeCallCapabilities(): Promise<NativeCallCapabilities | null> {
  if (!hasNativeCalls()) return null;
  try {
    const plugin = await import('@sableclient/tauri-plugin-livekit-mobile');
    return await plugin.getNativeCallCapabilities();
  } catch {
    return null;
  }
}

export const loadNativeCalls = async (): Promise<
  typeof import('@sableclient/tauri-plugin-livekit-mobile') | null
> => (hasNativeCalls() ? import('@sableclient/tauri-plugin-livekit-mobile') : null);

export type IncomingSystemCall = {
  callId: string;
  uuid: string;
  callerName: string;
  hasVideo: boolean;
  roomId: string;
};

export async function reportIncomingSystemCall(call: IncomingSystemCall): Promise<boolean> {
  const plugin = await loadNativeCalls();
  if (!plugin) return false;
  try {
    await plugin.reportIncomingCall(call);
    return true;
  } catch {
    return false;
  }
}

export function systemCallKey(callId: string, uuid: string): string {
  return isTauri() && osType() === 'ios' ? uuid : callId;
}

export async function endSystemCall(callId: string): Promise<boolean> {
  const plugin = await loadNativeCalls();
  if (!plugin) return false;
  try {
    await plugin.endSystemCall({ callId });
    return true;
  } catch {
    return false;
  }
}

export async function listenSystemCallActions(
  handler: (action: SystemCallAction) => void
): Promise<() => void> {
  const plugin = await loadNativeCalls();
  if (!plugin) return () => {};
  try {
    const listener = await plugin.onSystemCallAction(handler);
    for (const action of await plugin.drainPendingSystemCallActions()) handler(action);
    return () => void listener.unregister();
  } catch {
    return () => {};
  }
}

export async function fulfillSystemAnswer(uuid: string): Promise<void> {
  const plugin = await loadNativeCalls();
  await plugin?.fulfillAnswerCall(uuid).catch((error: unknown) => {
    console.debug('[sable call] system answer not fulfilled', error);
  });
}
